import { mkdir, readFile, readdir, stat, unlink, writeFile, appendFile } from "node:fs/promises";
import { join } from "node:path";
import { FileMemoryStore } from "./file-memory-store.js";

export interface RunLogEvent {
  timestamp: string;
  type:
    | "operation_start"
    | "operation_end"
    | "operation_error"
    | "workflow_start"
    | "workflow_end"
    | "task_start"
    | "task_end"
    | "handoff";
  runId: string;
  operationName?: string;
  agentId?: string;
  data?: unknown;
  durationMs?: number;
  error?: string;
}

export interface AgentLogWriterOptions {
  baseDir?: string;
}

export class AgentLogWriter {
  readonly baseDir: string;
  private memoryStore: FileMemoryStore;

  constructor(options?: AgentLogWriterOptions) {
    this.baseDir = options?.baseDir ?? ".agent-logs";
    this.memoryStore = new FileMemoryStore({
      baseDir: join(this.baseDir, "memories"),
    });
  }

  getMemoryStore(): FileMemoryStore {
    return this.memoryStore;
  }

  async writeRunEvent(runId: string, event: RunLogEvent): Promise<void> {
    const runsDir = join(this.baseDir, "runs");
    await mkdir(runsDir, { recursive: true });
    const filePath = join(runsDir, `${runId}.jsonl`);
    await appendFile(filePath, `${JSON.stringify(event)}\n`, "utf-8");
  }

  async readRunLog(runId: string): Promise<RunLogEvent[]> {
    const filePath = join(this.baseDir, "runs", `${runId}.jsonl`);
    try {
      const raw = await readFile(filePath, "utf-8");
      return raw
        .split("\n")
        .filter((line) => line.trim().length > 0)
        .map((line) => JSON.parse(line) as RunLogEvent);
    } catch (error) {
      if (
        error instanceof Error &&
        "code" in error &&
        (error as NodeJS.ErrnoException).code === "ENOENT"
      ) {
        return [];
      }
      throw error;
    }
  }

  async writeHandoff(
    runId: string,
    step: string,
    data: unknown,
  ): Promise<void> {
    const handoffsDir = join(this.baseDir, "handoffs");
    await mkdir(handoffsDir, { recursive: true });
    const filePath = join(handoffsDir, `${runId}_${step}.json`);
    await writeFile(filePath, JSON.stringify(data, null, 2), "utf-8");
  }

  async readHandoff(
    runId: string,
    step: string,
  ): Promise<unknown | null> {
    const filePath = join(this.baseDir, "handoffs", `${runId}_${step}.json`);
    try {
      const raw = await readFile(filePath, "utf-8");
      return JSON.parse(raw) as unknown;
    } catch (error) {
      if (
        error instanceof Error &&
        "code" in error &&
        (error as NodeJS.ErrnoException).code === "ENOENT"
      ) {
        return null;
      }
      throw error;
    }
  }

  async cleanOlderThan(maxAgeMs: number): Promise<{ deleted: number }> {
    const cutoff = Date.now() - maxAgeMs;
    let deleted = 0;

    const subdirs = ["memories", "runs", "handoffs"];
    for (const subdir of subdirs) {
      const dirPath = join(this.baseDir, subdir);
      let entries: string[];
      try {
        entries = await readdir(dirPath);
      } catch (error) {
        if (
          error instanceof Error &&
          "code" in error &&
          (error as NodeJS.ErrnoException).code === "ENOENT"
        ) {
          continue;
        }
        throw error;
      }

      for (const entry of entries) {
        const filePath = join(dirPath, entry);
        const fileStat = await stat(filePath);
        if (fileStat.mtimeMs < cutoff) {
          await unlink(filePath);
          deleted++;
        }
      }
    }

    return { deleted };
  }
}
